import React, { useMemo } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, Box, Chip, Divider, Grid,
} from '@mui/material';

interface EnvConfig {
    countries: { code: string; label: string; dialCode: string }[];
    currencies: { code: string; symbol: string; label: string }[];
    countryToCurrency: Record<string, string>;
    licenseTypes: string[];
    modules: string[];
    rootDomains: string[];
}

interface BusinessPlanViewDialogProps {
    open: boolean;
    onClose: () => void;
    plan: any | null;
    envConfig: EnvConfig | null;
}

export const BusinessPlanViewDialog: React.FC<BusinessPlanViewDialogProps> = ({ open, onClose, plan, envConfig }) => {
    if (!plan) return null;

    const countryLabel = useMemo(() => {
        if (!envConfig) return plan.country;
        const c = envConfig.countries.find(c => c.code === plan.country);
        return c ? `${c.code} - ${c.label}` : plan.country;
    }, [plan.country, envConfig]);

    const currencyCode = useMemo(() => {
        return envConfig?.countryToCurrency?.[plan.country] || 'GBP';
    }, [plan.country, envConfig]);

    const currencySymbol = useMemo(() => {
        const curr = envConfig?.currencies?.find(c => c.code === currencyCode);
        return curr?.symbol || currencyCode;
    }, [currencyCode, envConfig]);

    const formatPrice = (amount: number) => {
        const formatted = (amount / 100).toFixed(2);
        return `${currencySymbol}${formatted}`;
    };

    const renderRoutes = (routes: any[], label: string) => {
        if (!routes || routes.length === 0) return null;
        return (
            <Box mb={1}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
                <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                    {routes.map((r: any, idx: number) => (
                        <Chip key={idx} label={`${r.targetPlan} (${r.targetCycle})`} size="small" variant="outlined" />
                    ))}
                </Box>
            </Box>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="h6" fontWeight={700}>{plan.planName}</Typography>
                    <Chip label={plan.country} size="small" color="primary" sx={{ fontWeight: 600 }} />
                    {plan.rootDomain && (
                        <Chip label={plan.rootDomain} size="small" color="secondary" variant="outlined" sx={{ fontWeight: 600 }} />
                    )}
                </Box>
                {plan.tagline && (
                    <Typography variant="body2" color="text.secondary" mt={0.5}>{plan.tagline}</Typography>
                )}
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Country</Typography>
                        <Typography variant="body1">{countryLabel}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Root Domain</Typography>
                        <Typography variant="body1">{plan.rootDomain || '-'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Currency</Typography>
                        <Typography variant="body1">{currencyCode} ({currencySymbol})</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Tagline</Typography>
                        <Typography variant="body1">{plan.tagline || '-'}</Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle1" fontWeight={700} mb={1}>Pricing</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Monthly Price</Typography>
                        <Typography variant="body1" fontWeight={600}>
                            {formatPrice(plan.pricing?.monthlyPrice || 0)}/mo
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Annual Monthly Price</Typography>
                        <Typography variant="body1" fontWeight={600}>
                            {formatPrice(plan.pricing?.annualMonthlyPrice || 0)}/mo
                        </Typography>
                    </Grid>

                    {plan.productPlans && plan.productPlans.length > 0 && (
                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="subtitle1" fontWeight={700} mb={1}>
                                Product Plans ({plan.productPlans.length})
                            </Typography>
                            {plan.productPlans.map((pp: any, idx: number) => (
                                <Box
                                    key={idx}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        p: 1.5,
                                        mb: 1,
                                    }}
                                >
                                    <Box display="flex" gap={0.5} flexWrap="wrap" mb={1}>
                                        <Chip label={pp.module} size="small" color="primary" />
                                        <Chip label={pp.licenseType} size="small" variant="outlined" />
                                        <Chip label={`${pp.noOfUsers} user(s)`} size="small" variant="outlined" />
                                        {pp.durationMonths && (
                                            <Chip label={`${pp.durationMonths} month(s)`} size="small" variant="outlined" />
                                        )}
                                    </Box>
                                    <Grid container spacing={1}>
                                        {pp.whatsAppPerDay != null && pp.whatsAppPerDay > 0 && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">WhatsApp/day</Typography>
                                                <Typography variant="body2">{pp.whatsAppPerDay}</Typography>
                                            </Grid>
                                        )}
                                        {pp.whatsAppTotalCredit != null && pp.whatsAppTotalCredit > 0 && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">WhatsApp Total Credit</Typography>
                                                <Typography variant="body2">{pp.whatsAppTotalCredit}</Typography>
                                            </Grid>
                                        )}
                                        {pp.whatsAppTemplatePerDay != null && pp.whatsAppTemplatePerDay > 0 && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">WA Template/day</Typography>
                                                <Typography variant="body2">{pp.whatsAppTemplatePerDay}</Typography>
                                            </Grid>
                                        )}
                                        {pp.whatsAppTemplateTotalCredit != null && pp.whatsAppTemplateTotalCredit > 0 && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">WA Template Total Credit</Typography>
                                                <Typography variant="body2">{pp.whatsAppTemplateTotalCredit}</Typography>
                                            </Grid>
                                        )}
                                        {pp.whatsAppBusinessPerDay != null && pp.whatsAppBusinessPerDay > 0 && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">WA Business/day</Typography>
                                                <Typography variant="body2">{pp.whatsAppBusinessPerDay}</Typography>
                                            </Grid>
                                        )}
                                        {pp.whatsAppBusinessTotalCredit != null && pp.whatsAppBusinessTotalCredit > 0 && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">WA Business Total Credit</Typography>
                                                <Typography variant="body2">{pp.whatsAppBusinessTotalCredit}</Typography>
                                            </Grid>
                                        )}
                                        {pp.whatsAppBusinessTemplatePerDay != null && pp.whatsAppBusinessTemplatePerDay > 0 && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">WA Business Template/day</Typography>
                                                <Typography variant="body2">{pp.whatsAppBusinessTemplatePerDay}</Typography>
                                            </Grid>
                                        )}
                                        {pp.whatsAppBusinessTemplateTotalCredit != null && pp.whatsAppBusinessTemplateTotalCredit > 0 && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">WA Business Template Total Credit</Typography>
                                                <Typography variant="body2">{pp.whatsAppBusinessTemplateTotalCredit}</Typography>
                                            </Grid>
                                        )}
                                        {pp.gsmTextPerDay != null && pp.gsmTextPerDay > 0 && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">GSM Text/day</Typography>
                                                <Typography variant="body2">{pp.gsmTextPerDay}</Typography>
                                            </Grid>
                                        )}
                                        {pp.gsmTextTotalCredit != null && pp.gsmTextTotalCredit > 0 && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">GSM Text Total Credit</Typography>
                                                <Typography variant="body2">{pp.gsmTextTotalCredit}</Typography>
                                            </Grid>
                                        )}
                                        {pp.gsmTextTemplatePerDay != null && pp.gsmTextTemplatePerDay > 0 && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">GSM Text Template/day</Typography>
                                                <Typography variant="body2">{pp.gsmTextTemplatePerDay}</Typography>
                                            </Grid>
                                        )}
                                        {pp.gsmTextTemplateTotalCredit != null && pp.gsmTextTemplateTotalCredit > 0 && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">GSM Text Template Total Credit</Typography>
                                                <Typography variant="body2">{pp.gsmTextTemplateTotalCredit}</Typography>
                                            </Grid>
                                        )}
                                        {pp.telegramPerDay != null && pp.telegramPerDay > 0 && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Telegram/day</Typography>
                                                <Typography variant="body2">{pp.telegramPerDay}</Typography>
                                            </Grid>
                                        )}
                                        {pp.telegramTotalCredit != null && pp.telegramTotalCredit > 0 && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Telegram Total Credit</Typography>
                                                <Typography variant="body2">{pp.telegramTotalCredit}</Typography>
                                            </Grid>
                                        )}
                                        {pp.telegramTemplatePerDay != null && pp.telegramTemplatePerDay > 0 && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Telegram Template/day</Typography>
                                                <Typography variant="body2">{pp.telegramTemplatePerDay}</Typography>
                                            </Grid>
                                        )}
                                        {pp.telegramTemplateTotalCredit != null && pp.telegramTemplateTotalCredit > 0 && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Telegram Template Total Credit</Typography>
                                                <Typography variant="body2">{pp.telegramTemplateTotalCredit}</Typography>
                                            </Grid>
                                        )}
                                        {pp.emailPerDay != null && pp.emailPerDay > 0 && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Email/day</Typography>
                                                <Typography variant="body2">{pp.emailPerDay}</Typography>
                                            </Grid>
                                        )}
                                        {pp.emailTotalCredit != null && pp.emailTotalCredit > 0 && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Email Total Credit</Typography>
                                                <Typography variant="body2">{pp.emailTotalCredit}</Typography>
                                            </Grid>
                                        )}
                                        {pp.emailTemplatePerDay != null && pp.emailTemplatePerDay > 0 && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Email Template/day</Typography>
                                                <Typography variant="body2">{pp.emailTemplatePerDay}</Typography>
                                            </Grid>
                                        )}
                                        {pp.emailTemplateTotalCredit != null && pp.emailTemplateTotalCredit > 0 && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Email Template Total Credit</Typography>
                                                <Typography variant="body2">{pp.emailTemplateTotalCredit}</Typography>
                                            </Grid>
                                        )}
                                        {pp.senderHeaderName && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Sender Header</Typography>
                                                <Typography variant="body2">{pp.senderHeaderName}</Typography>
                                            </Grid>
                                        )}
                                        {pp.gsmWorkerName && (
                                            <Grid item xs={6} sm={4}>
                                                <Typography variant="caption" color="text.secondary">Worker Pool</Typography>
                                                <Typography variant="body2">{pp.gsmWorkerName}</Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Box>
                            ))}
                        </Grid>
                    )}

                    {(plan.upgradeRoutes?.monthly?.length > 0 || plan.upgradeRoutes?.annual?.length > 0 ||
                      plan.downgradeRoutes?.monthly?.length > 0 || plan.downgradeRoutes?.annual?.length > 0) && (
                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="subtitle1" fontWeight={700} mb={1}>Routes</Typography>
                            {renderRoutes(plan.upgradeRoutes?.monthly, 'Upgrade — Monthly')}
                            {renderRoutes(plan.upgradeRoutes?.annual, 'Upgrade — Annual')}
                            {renderRoutes(plan.downgradeRoutes?.monthly, 'Downgrade — Monthly')}
                            {renderRoutes(plan.downgradeRoutes?.annual, 'Downgrade — Annual')}
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Box display="flex" gap={3} flexWrap="wrap">
                            {plan.createdBy && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Created By</Typography>
                                    <Typography variant="body2">{plan.createdBy}</Typography>
                                </Box>
                            )}
                            {plan.createdAt && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Created At</Typography>
                                    <Typography variant="body2">
                                        {new Date(plan.createdAt).toLocaleString('en-GB', {
                                            day: '2-digit', month: '2-digit', year: 'numeric',
                                            hour: '2-digit', minute: '2-digit', hour12: false,
                                        })}
                                    </Typography>
                                </Box>
                            )}
                            {plan.modifiedBy && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Modified By</Typography>
                                    <Typography variant="body2">{plan.modifiedBy}</Typography>
                                </Box>
                            )}
                            {plan.updatedAt && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Updated At</Typography>
                                    <Typography variant="body2">
                                        {new Date(plan.updatedAt).toLocaleString('en-GB', {
                                            day: '2-digit', month: '2-digit', year: 'numeric',
                                            hour: '2-digit', minute: '2-digit', hour12: false,
                                        })}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" color="secondary" onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default BusinessPlanViewDialog;
